import React, { useCallback, useContext, useMemo } from "react";
import fp from "lodash/fp";
import moment from "moment";

import FiltersContext from "components/filters/filtersContext";
import { FiltersType, FilterFormatFn } from "types";

import styles from "./filterLabels.module.scss";

export type FilterLabelsProps = {
	className?: string;
	formatTime?: string;
	filters?: FiltersType;
	formatLabels?: Record<string, FilterFormatFn>;
	onRemove: (key: string, index?: number) => void;
	columns?: any[];
};

const RemoveIcon = () => {
	return <span className={styles.removeFilterBtn}>&#10006;</span>;
};

const FilterLabels = ({
	className,
	filters = {},
	formatLabels = {},
	formatTime = "MM/DD/YYYY",
	onRemove,
}: FilterLabelsProps) => {
	const { findColumn, isLabelForMultiValue } = useContext(FiltersContext);

	const formatValue = useCallback(
		(columnKey, value, column?: any) => {
			const valueHasFormatFn = !!formatLabels[columnKey];
			if (valueHasFormatFn) {
				const formatFn = formatLabels[columnKey];
				return formatFn(value, filters);
			} else if (value?.displayName) {
				return value.displayName;
			} else if (column?.getOptionLabel) {
				return column.getOptionLabel(value);
			} else if (value instanceof Date) {
				return moment(value).format(formatTime);
			} else {
				return value;
			}
		},
		[filters, formatLabels, formatTime],
	);

	const Label = useCallback(
		(filterKey, value, index: number = 0) => {
			const column = findColumn?.(filterKey);
			const key = filterKey + "_" + index;

			const printedLabel = column?.placeholder;
			const printedValue = formatValue(filterKey, value, column);

			if (printedValue) {
				return (
					<div
						key={key}
						className={styles.filterValue}
						onClick={() => onRemove(filterKey, index)}
					>
						<RemoveIcon />
						{printedLabel && <>{printedLabel}:&nbsp;</>}
						{printedValue}
					</div>
				);
			} else {
				return null;
			}
		},
		[findColumn, formatValue, onRemove],
	);

	const labels = useMemo(
		() =>
			fp.flow(
				Object.entries,
				fp.map(([key, values]) => {
					if (Array.isArray(values) && isLabelForMultiValue?.(key)) {
						return values?.map((v, index) => Label(key, v, index));
					} else {
						return Label(key, values);
					}
				}),
				fp.compact,
			)(filters),
		[Label, filters, isLabelForMultiValue],
	);

	return (
		<div className={className}>
			{fp.isEmpty(labels) ? (
				<strong className={styles.emptyValuesMessage}>
					The list has not yet been filtered
				</strong>
			) : (
				labels
			)}
		</div>
	);
};

export default FilterLabels;
